"use client";

import React, { useState, useRef } from "react";
import { toast } from "react-hot-toast";
import { payuConfig } from "@/lib/payu-config";

interface BookingData {
  id: string;
  classId: string;
  className: string;
  instructorName: string;
  classDate: string;
  status: string;
  paymentStatus: string;
  totalAmount: number;
}

interface ClassBookingProps {
  classData: {
    id: string;
    title: string;
    instructorName: string;
    price: number;
    capacity: number;
    currentEnrollment: number;
    duration: number;
    date?: string;
    description?: string;
  };
  userId: string;
  onBookingSuccess: (bookingData: BookingData) => void;
  onBookingError: (error: string) => void;
  onClose?: () => void;
}

const PaymentFirstBooking: React.FC<ClassBookingProps> = ({
  classData,
  userId,
  onBookingSuccess,
  onBookingError,
  onClose,
}) => {
  console.log("PaymentFirstBooking userId:", userId);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<"confirm" | "payment" | "processing">(
    "confirm"
  );
  const paymentFormRef = useRef<HTMLFormElement>(null);

  // Check if class is full
  const isClassFull = classData.currentEnrollment >= classData.capacity;

  const baseAmount = classData.price;
  const totalAmount = baseAmount;

  const handleBookClass = async () => {
    if (isClassFull) {
      toast.error("This class is full");
      return;
    }

    setIsLoading(true);
    setStep("payment");

    try {
      // Step 1: Create payment intent
      const paymentResponse = await fetch("/api/payments/create-order", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          classId: classData.id,
        }),
      });

      const paymentData = await paymentResponse.json();

      if (!paymentData.success) {
        throw new Error(paymentData.error || "Failed to create payment transaction");
      }

      // CHECK FOR SIMULATION
      if (paymentData.shouldSimulate && paymentData.bookingData) {
        console.log("🧪 Simulation mode active, bypassing PayU");
        setStep("processing");

        // Short delay to mimic processing time
        setTimeout(() => {
          onBookingSuccess(paymentData.bookingData);

          // Redirect handled by parent, but we can also trigger the success UI here if needed
          toast.success("Payment simulated successfully!");
        }, 1500);

        return;
      }

      // Step 2: Auto-submit PayU payment form
      // Create a hidden form with PayU parameters
      const form = paymentFormRef.current;
      if (form) {
        // Clear existing inputs
        form.innerHTML = "";

        // Add PayU parameters
        const params = paymentData.paymentParams;
        Object.keys(params).forEach((key) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = key;
          input.value = params[key];
          form.appendChild(input);
        });

        // Submit the form to PayU
        form.action = paymentData.paymentUrl || payuConfig.paymentUrl;
        form.method = "POST";
        form.submit();
      }
    } catch (error: unknown) {
      console.error("Payment initialization error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to initialize payment";
      toast.error(errorMessage);
      onBookingError(errorMessage);
      setIsLoading(false);
      setStep("confirm");
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case "confirm":
        return (
          <div className="space-y-4">
            {/* Class Information Display */}
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-4 bg-[#463a33]/5 rounded-xl">
                <svg
                  className="w-6 h-6 text-[#ff7043]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-[#463a33]">
                    {classData.title}
                  </p>
                  <p className="text-sm text-[#463a33]/70">
                    with {classData.instructorName}
                  </p>
                </div>
              </div>

              {classData.date && (() => {
                try {
                  const date = new Date(classData.date);
                  if (isNaN(date.getTime())) return null;

                  return (
                    <div className="flex items-center gap-3 p-4 bg-[#463a33]/5 rounded-xl">
                      <svg
                        className="w-6 h-6 text-[#ff7043]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-[#463a33]">
                          {date.toLocaleDateString()}
                        </p>
                        <p className="text-sm text-[#463a33]/70">
                          {date.toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                    </div>
                  );
                } catch (error) {
                  console.error("Invalid date in PaymentFirstBooking:", error);
                  return null;
                }
              })()}

              <div className="flex items-center gap-3 p-4 bg-[#463a33]/5 rounded-xl">
                <svg
                  className="w-6 h-6 text-[#ff7043]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-[#463a33]">Duration</p>
                  <p className="text-sm text-[#463a33]/70">
                    {classData.duration} minutes
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-[#463a33]/5 rounded-xl">
                <div className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-[#463a33]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  <span className="text-sm text-[#463a33]">
                    Spots Available
                  </span>
                </div>
                <span className="text-sm font-medium text-[#ff7043]">
                  {classData.capacity - classData.currentEnrollment} left
                </span>
              </div>
            </div>

            {/* Price Section */}
            <div className="bg-gray-50 border border-[#ff7043]/20 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-green-600"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Price
              </h3>

              <div className="space-y-2 text-sm">
                <div className="border-t pt-2 flex justify-between">
                  <span className="font-semibold text-gray-900">
                    Total Amount:
                  </span>
                  <span className="font-bold text-lg text-orange-600">
                    ₹{totalAmount.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {isClassFull ? (
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <p className="text-red-800 font-medium">
                  This class is currently full
                </p>
                <p className="text-red-600 text-sm">
                  {classData.currentEnrollment} / {classData.capacity} spots
                  taken
                </p>
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                <p className="text-green-800 text-sm">
                  {classData.capacity - classData.currentEnrollment} spots
                  available
                </p>
              </div>
            )}

            <button
              onClick={handleBookClass}
              disabled={isLoading || isClassFull}
              className={`w-full py-3 px-4 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${isClassFull
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-[#ff7043] hover:bg-[#ff7043]/90 text-white"
                }`}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : isClassFull ? (
                "Class Full"
              ) : (
                <>

                  Pay ₹{totalAmount.toLocaleString()} & Enroll
                </>
              )}
            </button>
          </div>
        );

      case "payment":
        return (
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ff7043] mx-auto"></div>
            <p className="text-[#463a33] font-medium">
              Redirecting to payment gateway...
            </p>
            <p className="text-sm text-[#463a33]/70">
              Please complete your payment to confirm booking
            </p>
          </div>
        );

      case "processing":
        return (
          <div className="text-center space-y-4">
            <div className="animate-pulse">
              <div className="rounded-full bg-green-100 h-16 w-16 flex items-center justify-center mx-auto">
                <svg
                  className="h-8 w-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
              </div>
            </div>
            <p className="text-[#463a33] font-medium">
              Payment successful! Creating your booking...
            </p>
            <p className="text-sm text-[#463a33]/70">
              This will only take a moment
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-white w-full overflow-hidden zoom-container">
      {/* Hidden PayU form */}
      <form ref={paymentFormRef} style={{ display: "none" }} />

      <div className="p-4 sm:p-6 pb-32 sm:pb-6">
        <div className="flex justify-between items-start mb-4 sm:mb-6">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-[#463a33]">
              Enroll in Class
            </h2>
            <p className="text-[#463a33]/70 text-xs sm:text-sm mt-1">
              Secure payment • Instant confirmation
            </p>
          </div>
          <button
            onClick={() => {
              // Use onClose if available, otherwise fallback to onBookingError
              if (onClose) {
                onClose();
              } else {
                onBookingError("Booking cancelled");
              }
            }}
            className="text-[#463a33]/60 hover:text-[#463a33] transition-colors p-1 -mr-1"
            aria-label="Close"
          >
            <svg
              className="w-5 h-5 sm:w-6 sm:h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {renderStepContent()}

        {step === "confirm" && (
          <div className="mt-4 sm:mt-6 text-xs text-[#463a33]/60 text-center space-y-1">
            <p className="flex items-center justify-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 1l3.09 6.26L22 9l-5 4.87L18.18 21 12 17.77 5.82 21 7 13.87 2 9l6.91-1.74L12 1z" />
              </svg>
              Secure payment powered by PayU
            </p>

          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentFirstBooking;
